import React, { useState, useEffect } from 'react';

const LandingPage = ({ onNavigateToLogin, onNavigateToTryProduct, onNavigateToPricing }) => {
  const [scrollY, setScrollY] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load dark mode preference from localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setIsDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const features = [
    {
      icon: '📊',
      title: 'Analisis Laporan Keuangan Otomatis',
      description: 'Upload laporan keuangan Anda dan dapatkan analisis mendalam secara instan dengan teknologi AI terdepan.'
    },
    {
      icon: '🤖',
      title: 'AI-Powered Insights',
      description: 'Dapatkan wawasan bisnis yang actionable dengan analisis cerdas menggunakan Google Gemini dan Groq/Llama.'
    },
    {
      icon: '📈',
      title: 'Visualisasi Data Interaktif',
      description: 'Lihat tren keuangan Anda melalui grafik dan dashboard yang mudah dipahami dan informatif.'
    },
    {
      icon: '🛡️',
      title: 'Fraud Detection',
      description: 'Deteksi transaksi mencurigakan secara otomatis untuk melindungi bisnis Anda dari potensi risiko.'
    },
    {
      icon: '💬',
      title: 'AI Assistant',
      description: 'Konsultasikan pertanyaan keuangan Anda dengan asisten AI yang siap membantu 24/7.'
    },
    {
      icon: '📋',
      title: 'Audit Plan Analysis',
      description: 'Analisis dokumen regulasi dan internal untuk memastikan compliance dan audit yang lebih efisien.'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 overflow-x-hidden transition-colors duration-300">
      {/* Navigation Bar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrollY > 50
          ? 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
        }`}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <img
                src="/lg_tabulara.png"
                alt="Tabulara"
                className={`h-8 object-contain ${isDarkMode ? 'filter brightness-0 invert' : ''}`}
              />
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors text-sm font-medium">
                Fitur
              </a>
              <a href="#pricing" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors text-sm font-medium">
                Harga
              </a>
              <button
                onClick={onNavigateToLogin}
                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors text-sm font-medium"
              >
                Masuk
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? (
                  <i className="fa-solid fa-sun text-lg"></i>
                ) : (
                  <i className="fa-solid fa-moon text-lg"></i>
                )}
              </button>
              <button
                onClick={onNavigateToTryProduct}
                className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-full text-sm font-medium hover:bg-blue-700 dark:hover:bg-blue-600 transition-all duration-200 transform hover:scale-105"
              >
                Coba Sekarang
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 sm:px-8 lg:px-12 overflow-hidden">
        {/* CSS Animations for Gradient Movement */}
        <style>{`
          @keyframes float1 {
            0%, 100% {
              transform: translate(0, 0) scale(1);
            }
            33% {
              transform: translate(30px, -30px) scale(1.1);
            }
            66% {
              transform: translate(-20px, 20px) scale(0.95);
            }
          }
          
          @keyframes float2 {
            0%, 100% {
              transform: translate(0, 0) scale(1);
            }
            50% {
              transform: translate(-40px, 40px) scale(1.15);
            }
          }
          
          @keyframes float3 {
            0%, 100% {
              transform: translate(-50%, -50%) scale(1);
            }
            25% {
              transform: translate(-45%, -55%) scale(1.05);
            }
            50% {
              transform: translate(-55%, -45%) scale(0.98);
            }
            75% {
              transform: translate(-48%, -52%) scale(1.02);
            }
          }
          
          @keyframes float4 {
            0%, 100% {
              transform: translate(0, 0) scale(1);
            }
            33% {
              transform: translate(25px, -25px) scale(1.08);
            }
            66% {
              transform: translate(-30px, 30px) scale(0.92);
            }
          }
          
          @keyframes float5 {
            0%, 100% {
              transform: translate(0, 0) scale(1);
            }
            40% {
              transform: translate(35px, 35px) scale(1.12);
            }
            80% {
              transform: translate(-25px, -25px) scale(0.94);
            }
          }
          
          .gradient-float-1 {
            animation: float1 20s ease-in-out infinite;
          }
          
          .gradient-float-2 {
            animation: float2 25s ease-in-out infinite;
          }
          
          .gradient-float-3 {
            animation: float3 30s ease-in-out infinite;
          }
          
          .gradient-float-4 {
            animation: float4 22s ease-in-out infinite;
          }
          
          .gradient-float-5 {
            animation: float5 28s ease-in-out infinite;
          }
        `}</style>

        {/* Ambience Gradient Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Gradient Circle 1 - Top Left */}
          <div className="gradient-float-1 absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-blue-400/30 to-blue-600/20 dark:from-blue-500/20 dark:to-blue-700/10 rounded-full blur-3xl"></div>

          {/* Gradient Circle 2 - Top Right */}
          <div className="gradient-float-2 absolute -top-20 -right-20 w-80 h-80 bg-gradient-to-br from-purple-400/25 to-blue-500/20 dark:from-purple-500/15 dark:to-blue-600/10 rounded-full blur-3xl"></div>

          {/* Gradient Circle 3 - Center */}
          <div className="gradient-float-3 absolute top-1/2 left-1/2 w-[600px] h-[600px] bg-gradient-to-br from-blue-300/20 via-purple-300/15 to-blue-500/20 dark:from-blue-600/10 dark:via-purple-600/8 dark:to-blue-700/10 rounded-full blur-3xl"></div>

          {/* Gradient Circle 4 - Bottom Left */}
          <div className="gradient-float-4 absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-tr from-blue-500/25 to-cyan-400/20 dark:from-blue-600/15 dark:to-cyan-500/10 rounded-full blur-3xl"></div>

          {/* Gradient Circle 5 - Bottom Right */}
          <div className="gradient-float-5 absolute -bottom-20 -right-20 w-96 h-96 bg-gradient-to-tl from-purple-500/20 to-blue-400/25 dark:from-purple-600/12 dark:to-blue-500/15 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-6xl sm:text-7xl md:text-8xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight transition-colors duration-300">
              Analisis Laporan
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-400 dark:to-blue-300 bg-clip-text text-transparent">
                Keuangan
              </span>
              <br />
              yang Cerdas
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed transition-colors duration-300">
              Transformasikan data keuangan Anda menjadi wawasan bisnis yang actionable
              <br />
              dengan teknologi AI terdepan
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <button
                onClick={onNavigateToTryProduct}
                className="px-8 py-4 bg-blue-600 dark:bg-blue-500 text-white rounded-full text-lg font-semibold hover:bg-blue-700 dark:hover:bg-blue-600 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Coba Produk Kami
              </button>
              <button
                onClick={onNavigateToPricing}
                className="px-8 py-4 bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-2 border-blue-600 dark:border-blue-500 rounded-full text-lg font-semibold hover:bg-blue-50 dark:hover:bg-gray-700 transition-all duration-200 transform hover:scale-105"
              >
                Lihat Harga & Paket
              </button>
            </div>
          </div>

          {/* Hero Visual */}
          <div className="relative z-10 max-w-5xl mx-auto">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-blue-50 to-white dark:from-gray-800 dark:to-gray-900 border border-gray-100 dark:border-gray-700 transition-colors duration-300">
              <div className="aspect-video bg-gradient-to-br from-blue-100 via-white to-blue-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center transition-colors duration-300">
                <img
                  src={`${process.env.PUBLIC_URL}/lk.png`}
                  alt="Financial Analysis Dashboard"
                  className="rounded-2xl shadow-2xl absolute inset-0 w-[800px] h-[500px] object-cover transition-opacity duration-500 hover:opacity-0"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 px-6 sm:px-8 lg:px-12 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
              Fitur Unggulan
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto transition-colors duration-300">
              Semua yang Anda butuhkan untuk analisis keuangan yang komprehensif
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-900 rounded-2xl p-8 hover:shadow-xl dark:hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 dark:border-gray-700"
              >
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed transition-colors duration-300">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-32 px-6 sm:px-8 lg:px-12 bg-white dark:bg-gray-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
              Pilih Paket yang Tepat
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto transition-colors duration-300">
              Solusi untuk setiap kebutuhan bisnis Anda
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Starter Plan */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-300">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">Starter</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900 dark:text-white transition-colors duration-300">Rp 500K</span>
                <span className="text-gray-600 dark:text-gray-400 transition-colors duration-300">/bulan</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <i className="fa-solid fa-check text-blue-600 dark:text-blue-400 mr-3 mt-1"></i>
                  <span className="text-gray-600 dark:text-gray-300 transition-colors duration-300">10 analisis per bulan</span>
                </li>
                <li className="flex items-start">
                  <i className="fa-solid fa-check text-blue-600 dark:text-blue-400 mr-3 mt-1"></i>
                  <span className="text-gray-600 dark:text-gray-300 transition-colors duration-300">AI Assistant dasar</span>
                </li>
                <li className="flex items-start">
                  <i className="fa-solid fa-check text-blue-600 dark:text-blue-400 mr-3 mt-1"></i>
                  <span className="text-gray-600 dark:text-gray-300 transition-colors duration-300">Dashboard standar</span>
                </li>
              </ul>
              <button
                onClick={onNavigateToPricing}
                className="w-full py-3 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-full font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
              >
                Pelajari Lebih Lanjut
              </button>
            </div>

            {/* Professional Plan - Featured */}
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 border-2 border-blue-600 transform scale-105 shadow-2xl">
              <div className="bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4">
                POPULER
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Professional</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">Rp 1.5JT</span>
                <span className="text-blue-100">/bulan</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <i className="fa-solid fa-check text-white mr-3 mt-1"></i>
                  <span className="text-white">Analisis unlimited</span>
                </li>
                <li className="flex items-start">
                  <i className="fa-solid fa-check text-white mr-3 mt-1"></i>
                  <span className="text-white">AI Assistant premium</span>
                </li>
                <li className="flex items-start">
                  <i className="fa-solid fa-check text-white mr-3 mt-1"></i>
                  <span className="text-white">Fraud detection</span>
                </li>
                <li className="flex items-start">
                  <i className="fa-solid fa-check text-white mr-3 mt-1"></i>
                  <span className="text-white">Audit plan analysis</span>
                </li>
                <li className="flex items-start">
                  <i className="fa-solid fa-check text-white mr-3 mt-1"></i>
                  <span className="text-white">Priority support</span>
                </li>
              </ul>
              <button
                onClick={onNavigateToPricing}
                className="w-full py-3 bg-white text-blue-600 rounded-full font-semibold hover:bg-blue-50 transition-all duration-200"
              >
                Mulai Sekarang
              </button>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-300">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">Enterprise</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900 dark:text-white transition-colors duration-300">Custom</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <i className="fa-solid fa-check text-blue-600 dark:text-blue-400 mr-3 mt-1"></i>
                  <span className="text-gray-600 dark:text-gray-300 transition-colors duration-300">Semua fitur Professional</span>
                </li>
                <li className="flex items-start">
                  <i className="fa-solid fa-check text-blue-600 dark:text-blue-400 mr-3 mt-1"></i>
                  <span className="text-gray-600 dark:text-gray-300 transition-colors duration-300">Custom integration</span>
                </li>
                <li className="flex items-start">
                  <i className="fa-solid fa-check text-blue-600 dark:text-blue-400 mr-3 mt-1"></i>
                  <span className="text-gray-600 dark:text-gray-300 transition-colors duration-300">Dedicated support</span>
                </li>
                <li className="flex items-start">
                  <i className="fa-solid fa-check text-blue-600 dark:text-blue-400 mr-3 mt-1"></i>
                  <span className="text-gray-600 dark:text-gray-300 transition-colors duration-300">SLA guarantee</span>
                </li>
              </ul>
              <button
                onClick={onNavigateToPricing}
                className="w-full py-3 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-full font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
              >
                Hubungi Sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 sm:px-8 lg:px-12 bg-gradient-to-br from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 transition-colors duration-300">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl sm:text-6xl font-bold text-white mb-6">
            Siap Memulai?
          </h2>
          <p className="text-xl text-blue-100 dark:text-blue-200 mb-12 max-w-2xl mx-auto transition-colors duration-300">
            Bergabunglah dengan ribuan bisnis yang telah mempercayai Tabulara untuk analisis keuangan mereka
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={onNavigateToTryProduct}
              className="px-8 py-4 bg-white dark:bg-gray-100 text-blue-600 dark:text-blue-700 rounded-full text-lg font-semibold hover:bg-blue-50 dark:hover:bg-white transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              Coba Gratis Sekarang
            </button>
            <button
              onClick={onNavigateToPricing}
              className="px-8 py-4 bg-transparent text-white border-2 border-white rounded-full text-lg font-semibold hover:bg-white/10 dark:hover:bg-white/20 transition-all duration-200"
            >
              Lihat Paket & Harga
            </button>
          </div>
        </div>
      </section>

      {/* Footer with Login Link */}
      <footer className="bg-gray-900 text-gray-400 py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div>
              <img
                src={`${process.env.PUBLIC_URL}/lg_tabulara.png`}
                alt="Tabulara Logo"
                className="h-10 object-contain dark:brightness-0 dark:invert transition-all duration-300"
              />
              <p className="text-sm">
                Platform analisis laporan keuangan berbasis AI untuk bisnis modern.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Produk</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Fitur</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Harga</a></li>
                <li><button onClick={onNavigateToTryProduct} className="hover:text-white transition-colors">Coba Sekarang</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Perusahaan</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="https://dssolution.id/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Tentang Kami</a></li>
                <li><a href="https://dssolution.id/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Kontak</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Akun</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={onNavigateToLogin} className="hover:text-white transition-colors">
                    Masuk
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <p className="text-sm mb-4 md:mb-0">
                © {new Date().getFullYear()} Tabulara. All rights reserved.
              </p>
              <div className="text-sm">
                <span className="text-gray-500">Sudah punya akses? </span>
                <button
                  onClick={onNavigateToLogin}
                  className="text-blue-400 hover:text-blue-300 font-semibold transition-colors underline"
                >
                  Masuk di sini
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;

